/*
  Mewse "Printed With Love" page sections (mewse-pwl-*).
  - Videos show their poster first, then load: `data-load="page"` after the page has loaded,
    anything else just before it scrolls into view.
  - Tabs: a list of details that swaps the image beside it.
  - Slides: a swipeable deck; a slide's video (`data-load="slide"`) plays only while it's showing.
  - Once loaded, a video pauses whenever it's scrolled out of view. With Data Saver on (or
    reduced motion), videos never load and the poster stays.
  Every section includes this file, so it only sets itself up once, and re-runs on a
  section when the theme editor reloads it.
*/
(function () {
  if (window.MewsePwl) return;

  const isMobile = window.matchMedia('(max-width: 767px)').matches;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = !!(navigator.connection && navigator.connection.saveData);
  const noVideo = reducedMotion || saveData;

  // Pause loaded videos while they're off screen (slide videos are handled by their deck)
  const viewObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          video.dataset.inView = entry.isIntersecting ? 'true' : '';
          if (!video.dataset.loaded) return;
          if (entry.isIntersecting) video.play().catch(() => {});
          else video.pause();
        });
      })
    : null;

  function startVideo(video) {
    if (video.dataset.loaded) return;
    video.dataset.loaded = 'true';
    video.src = video.dataset.src;
    video.addEventListener('canplay', () => {
      video.classList.add('is-visible');
      // Off screen by the time it's ready (e.g. scrolled past quickly): wait until it's back
      if (video.dataset.load !== 'slide' && viewObserver && video.dataset.inView !== 'true') return;
      video.play().catch(() => {});
    }, { once: true });
    video.load();
  }

  const scrollObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          startVideo(entry.target);
          scrollObserver.unobserve(entry.target);
        });
      }, { rootMargin: '400px 0px' })
    : null;

  function initVideos(root) {
    root.querySelectorAll('.mewse-pwl-lazy-video').forEach((video) => {
      if (video.dataset.ready) return;
      video.dataset.ready = 'true';
      if (noVideo || !video.dataset.src) return;
      if (video.dataset.load === 'slide') return; // started by initSlides
      if (viewObserver) viewObserver.observe(video);
      if (isMobile && video.dataset.mobile !== 'true') return;
      if (video.dataset.load === 'page') {
        const go = () => ('requestIdleCallback' in window)
          ? requestIdleCallback(() => startVideo(video), { timeout: 2000 })
          : setTimeout(() => startVideo(video), 300);
        if (document.readyState === 'complete') go();
        else window.addEventListener('load', go, { once: true });
      } else if (scrollObserver) {
        scrollObserver.observe(video);
      } else {
        startVideo(video);
      }
    });
  }

  function initTabs(root) {
    root.querySelectorAll('[data-pwl-tabs]').forEach((group) => {
      if (group.dataset.ready) return;
      group.dataset.ready = 'true';
      const tabs = Array.from(group.querySelectorAll('.mewse-pwl-tab'));
      const panels = group.querySelectorAll('.mewse-pwl-panel');
      if (!tabs.length) return;

      function select(tab, focus) {
        tabs.forEach((t) => {
          const active = t === tab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
          t.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel) => {
          const active = panel.id === tab.getAttribute('aria-controls');
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
        });
        if (focus) tab.focus();
      }

      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab, false));
        tab.addEventListener('keydown', (e) => {
          let next = null;
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
          if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
          if (e.key === 'Home') next = tabs[0];
          if (e.key === 'End') next = tabs[tabs.length - 1];
          if (next) { e.preventDefault(); select(next, true); }
        });
      });

      group.mewseSelectBlock = (blockId) => {
        const tab = group.querySelector('.mewse-pwl-tab[data-block-id="' + blockId + '"]');
        if (tab) select(tab, false);
      };
    });
  }

  function initSlides(root) {
    root.querySelectorAll('[data-pwl-slides]').forEach((deck) => {
      if (deck.dataset.ready) return;
      deck.dataset.ready = 'true';
      const track = deck.querySelector('.mewse-pwl-deck-track');
      const slides = Array.from(deck.querySelectorAll('.mewse-pwl-slide'));
      const dots = Array.from(deck.querySelectorAll('.mewse-pwl-deck-dot'));
      if (!track || !slides.length) return;
      let current = 0;
      let aimed = null; // slide a button is scrolling to, so quick repeat taps keep counting from it
      let onScreen = false;

      function syncVideos() {
        slides.forEach((slide, i) => {
          const video = slide.querySelector('.mewse-pwl-lazy-video');
          if (!video) return;
          if (i === current && onScreen && !noVideo) {
            if (video.dataset.loaded) video.play().catch(() => {});
            else startVideo(video);
          } else if (!video.paused) {
            video.pause();
          }
        });
      }

      function setCurrent(i) {
        if (i === aimed) aimed = null;
        if (i === current) return;
        current = i;
        dots.forEach((dot, d) => {
          dot.classList.toggle('is-active', d === i);
          if (d === i) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        syncVideos();
      }

      function goTo(i) {
        const n = slides.length;
        const target = ((i % n) + n) % n;
        aimed = target;
        track.scrollTo({ left: slides[target].offsetLeft - track.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
      }

      // A swipe cancels any button-led scroll
      track.addEventListener('pointerdown', () => { aimed = null; });
      track.addEventListener('touchstart', () => { aimed = null; }, { passive: true });

      let ticking = false;
      track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          setCurrent(Math.round(track.scrollLeft / track.clientWidth));
        });
      });

      deck.querySelectorAll('.mewse-pwl-deck-arrow').forEach((btn) => {
        btn.addEventListener('click', () => goTo((aimed ?? current) + Number(btn.dataset.dir)));
      });
      dots.forEach((dot) => dot.addEventListener('click', () => goTo(Number(dot.dataset.index))));
      track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo((aimed ?? current) + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo((aimed ?? current) - 1); }
      });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          onScreen = entries[0].isIntersecting;
          syncVideos();
        }, { rootMargin: '200px 0px' }).observe(deck);
      } else {
        onScreen = true;
        syncVideos();
      }

      deck.mewseSyncVideos = syncVideos;

      deck.mewseSelectBlock = (blockId) => {
        const i = slides.findIndex((s) => s.dataset.blockId === blockId);
        if (i > -1) goTo(i);
      };
    });
  }

  function init(root) {
    initVideos(root);
    initTabs(root);
    initSlides(root);
  }

  window.MewsePwl = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // Theme editor: set up sections as they're added or changed, and show the selected tab
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const group = e.target.closest('[data-pwl-tabs], [data-pwl-slides]');
    if (group && group.mewseSelectBlock) group.mewseSelectBlock(e.detail.blockId);
  });

  // Browsers hold back playback in hidden tabs; start any loaded video when the tab is shown
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    document.querySelectorAll('.mewse-pwl-lazy-video.is-visible').forEach((video) => {
      if (!video.paused) return;
      if (video.dataset.load === 'slide') {
        const deck = video.closest('[data-pwl-slides]');
        if (deck && deck.mewseSyncVideos) deck.mewseSyncVideos();
      } else if (!viewObserver || video.dataset.inView === 'true') {
        video.play().catch(() => {});
      }
    });
  });
})();
